import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Menu, Search, X, Share2 } from 'lucide-react'; // Added Share2
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

const TopBar = ({ onShareClick }) => { // Added onShareClick prop
  const { theme } = useTheme();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [desktopSearchQuery, setDesktopSearchQuery] = useState("");
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const [searchParams] = useSearchParams(); // To read URL search parameters
  const location = useLocation();

  // Close mobile menu on outside click
  // Effect to sync desktopSearchQuery with URL and manage search bar visibility
  useEffect(() => {
    const queryFromUrl = searchParams.get("search") || "";
    setDesktopSearchQuery(queryFromUrl);
    if (queryFromUrl) {
      setShowDesktopSearch(true); // If URL has a search term, ensure search bar is visible
    }
    // If queryFromUrl is empty, we don't automatically hide the search bar here.
    // It remains visible if the user had it open, allowing them to type a new query.
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(event.target) && !event.target.closest('#desktop-search-toggle')) {
        setShowDesktopSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDesktopSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = desktopSearchQuery.trim();
    if (trimmedQuery) {
      navigate(`/resources?search=${encodeURIComponent(trimmedQuery)}`);
      // The useEffect hook listening to searchParams will keep the bar open and populated.
    } else {
      // If the input was empty or just spaces, and if there's an existing search in the URL,
      // navigate to /resources to clear it. Otherwise, do nothing if URL is already clean.
      if (searchParams.get("search")) {
        navigate('/resources');
      }
    }
  };

  return (
    <header 
      className="sticky top-0 z-50 md-elevation-2 flex items-center justify-between px-3 sm:px-4 h-14 sm:h-16 touch-manipulation transition-colors"
      style={{
        backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff'
      }}
    >
      {/* Left: Branding */}
      <Link to="/" className="flex items-center gap-2 sm:gap-3 focus:outline-none focus:ring-2 focus:ring-green-600 rounded-md p-1 -ml-1">
        <img
          src="/PadhoJalnaLogo.png"
          alt="Padho Jalna Logo"
          className="h-8 w-auto"
        />
        <h1 
          className="text-lg font-semibold select-none hidden sm:block"
          style={{
            color: theme === 'dark' ? '#ffffff' : '#1f2937'
          }}
        >
          Padho Jalna
        </h1>
      </Link>

      {/* Right: Actions (all in one flex row, menu last) */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Desktop Search */}
        <div className="hidden lg:flex items-center relative" ref={desktopSearchRef}>
          {!showDesktopSearch && (
            <button
              id="desktop-search-toggle"
              onClick={() => setShowDesktopSearch(true)}
              aria-label="Open search"
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-600"
            >
              <Search size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          )}
          {showDesktopSearch && (
            <form onSubmit={handleDesktopSearchSubmit} className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-md p-1 shadow-sm">
              <Search size={18} className="text-gray-500 dark:text-gray-400 mx-1" />
              <input
                type="search"
                value={desktopSearchQuery}
                onChange={(e) => setDesktopSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="px-2 py-1 text-sm bg-transparent text-gray-900 dark:text-white focus:outline-none w-48 transition-all duration-300 placeholder-gray-500 dark:placeholder-gray-400"
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setDesktopSearchQuery(""); // Clear local input
                  navigate('/resources');    // Navigate to clear URL search param
                }}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            </form>
          )}
        </div>

        {/* Favorites Link (Desktop/Tablet) */}
        <Link
          to="/favorites"
          aria-label="Favorites"
          className="p-2 rounded-md hover:bg-yellow-100 dark:hover:bg-yellow-900/30 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.388 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.388-2.46a1 1 0 00-1.176 0l-3.388 2.46c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.388-2.46c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.967z" />
          </svg>
          <span className="ml-1 hidden sm:inline text-yellow-700 font-medium">Favorites</span>
        </Link>

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Share Button (Primary Action) */}
        <button
          onClick={onShareClick}
          aria-label="Share this page"
          className="bg-blue-600 text-white rounded-full p-2 shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
        >
          <Share2 size={22} />
        </button>
        {/* Mobile Search Icon (links to resources page) */}
        {location.pathname !== '/resources' && (
          <Link to="/resources"
            aria-label="Search resources"
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-600 lg:hidden"
          >
            <Search size={20} className="text-gray-600 dark:text-gray-300" />
          </Link>
        )}

        {/* Mobile Menu Button (always last) */}
        <div className="relative lg:hidden" ref={mobileMenuRef}>
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Open menu"
            aria-expanded={showMobileMenu}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            {showMobileMenu ? <X size={20} className="text-gray-600 dark:text-gray-300" /> : <Menu size={20} className="text-gray-600 dark:text-gray-300" />}
          </button>
          {showMobileMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl ring-1 ring-black dark:ring-gray-700 ring-opacity-5 py-2 z-50 border-2 border-blue-100 dark:border-gray-700 animate-dropdown">
              {/* Secondary links only (no Home, Projects, Resources, Donate) */}
              <span className="block px-4 py-2 text-sm text-gray-300 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 cursor-not-allowed select-none rounded-lg mb-1 border border-dashed border-gray-200 dark:border-gray-600 opacity-70" tabIndex={-1} aria-disabled="true">About Us</span>
              <span className="block px-4 py-2 text-sm text-gray-300 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 cursor-not-allowed select-none rounded-lg mb-1 border border-dashed border-gray-200 dark:border-gray-600 opacity-70" tabIndex={-1} aria-disabled="true">Contact</span>
              <Link to="/favorites" onClick={() => setShowMobileMenu(false)} className="block px-4 py-2 text-sm text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 hover:text-yellow-800 dark:hover:text-yellow-300 rounded-lg">Favorites</Link>
              <Link to="/admin/login" onClick={() => setShowMobileMenu(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-green-600 dark:hover:text-green-400 rounded-lg">Admin Login</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
